/**
 * Pins product(s) to the top of collection grids.
 * Supports:
 *   ?featured_product=handle
 *   ?featured_product=h1&featured_product=h2  (up to 20)
 *   ?featured_product=h1,h2
 *   ?p1=h1&p2=h2&...&p20=h20  (exclusive alternative; no ad badge)
 * Fetches card HTML from the Section Rendering API (/products/{handle}?section_id=...)
 * and prepends it to the collection grid.
 */
(function () {
  if (window.FeaturedProductCollection) {
    return;
  }

  var PARAM = 'featured_product';
  var MAX_PINNED_PRODUCTS = 20;
  var MAX_AUTO_PAGES = 60;
  var FEATURED_SECTION_ID = 'featured-collection-product-card-fetch';
  var FEATURED_SECTION_SPLIT_ID = 'featured-collection-product-card-fetch-split';
  var FEATURED_SECTION_VIDEO_SPLIT_ID = 'featured-collection-product-card-fetch-video-split';
  var FEATURED_EXTERNAL_SECTION_ID = 'featured-collection-external-product';
  var FEATURED_EXTERNAL_SECTION_SPLIT_ID = 'featured-collection-external-product-split';
  var FEATURED_EXTERNAL_VIDEO_SPLIT_ID = 'featured-collection-external-video-split';
  var prefetchCache = {};
  var ensurePromises = {};

  function normalizeHandle(handle) {
    return (handle || '').trim().toLowerCase();
  }

  function isPParamMode() {
    var params = new URLSearchParams(window.location.search);
    var featuredValues = params.getAll(PARAM);
    for (var f = 0; f < featuredValues.length; f++) {
      if ((featuredValues[f] || '').trim()) {
        return false;
      }
    }

    for (var i = 1; i <= MAX_PINNED_PRODUCTS; i++) {
      if ((params.get('p' + i) || '').trim()) {
        return true;
      }
    }

    return false;
  }

  function getFeaturedHandles() {
    var params = new URLSearchParams(window.location.search);
    var handles = [];
    var seen = {};

    function addHandle(handle) {
      handle = (handle || '').trim();
      if (!handle) return;
      var key = normalizeHandle(handle);
      if (seen[key] || handles.length >= MAX_PINNED_PRODUCTS) return;
      seen[key] = true;
      handles.push(handle);
    }

    // featured_product (single, repeated, or comma-separated) takes priority over p1-p20
    var featuredValues = params.getAll(PARAM);
    var hasFeatured = false;
    for (var f = 0; f < featuredValues.length; f++) {
      if ((featuredValues[f] || '').trim()) {
        hasFeatured = true;
        break;
      }
    }

    if (hasFeatured) {
      for (var i = 0; i < featuredValues.length; i++) {
        String(featuredValues[i] || '')
          .split(',')
          .forEach(addHandle);
      }
      return handles;
    }

    for (var p = 1; p <= MAX_PINNED_PRODUCTS; p++) {
      addHandle(params.get('p' + p));
    }

    return handles;
  }

  function getPinnedHandleIndex(handle) {
    var handles = getFeaturedHandles();
    var target = normalizeHandle(handle);
    for (var i = 0; i < handles.length; i++) {
      if (normalizeHandle(handles[i]) === target) {
        return i;
      }
    }
    return -1;
  }

  function getShopifyRoot() {
    if (window.Shopify && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root;
    }
    return '/';
  }

  function isFilteringOrSortingActive(productList) {
    var params = new URLSearchParams(window.location.search);
    var defaultSort =
      productList && productList.getAttribute
        ? productList.getAttribute('data-collection-default-sort')
        : '';
    var keys = params.keys();
    var key = keys.next();

    while (!key.done) {
      if (key.value.indexOf('filter.') === 0) {
        return true;
      }

      if (key.value === 'sort_by') {
        var sortBy = params.get('sort_by');
        if (!sortBy || (defaultSort && sortBy === defaultSort)) {
          key = keys.next();
          continue;
        }
        return true;
      }

      key = keys.next();
    }

    return false;
  }

  function isDisabled(productList) {
    return !productList || productList.getAttribute('data-featured-product-disabled') === 'true';
  }

  function isPlaceholderCard(card) {
    return !!(card && card.hasAttribute('data-featured-product-placeholder'));
  }

  function isRealProductCard(card) {
    return !!(card && !isPlaceholderCard(card));
  }

  function getCardHandle(card) {
    return normalizeHandle(card.getAttribute('handle'));
  }

  function cardMatchesProductHandle(card, handle) {
    var target = normalizeHandle(handle);
    if (!target || !card) {
      return false;
    }

    if (getCardHandle(card) === target) {
      return true;
    }

    var splitProductHandle = normalizeHandle(card.getAttribute('data-split-product-handle'));
    if (splitProductHandle === target) {
      return true;
    }

    var featuredProductHandle = normalizeHandle(card.getAttribute('data-featured-product-handle'));
    if (featuredProductHandle === target && card.getAttribute('data-featured-product') === 'true') {
      return true;
    }

    // Variant card handles look like "{product-handle}-{variantId}".
    // Do NOT treat a longer product handle as a match
    // (e.g. "l-etoile-royale" must not match "l-etoile-royale-earrings-0-5-carat-123").
    var cardHandle = getCardHandle(card);
    if (cardHandle.indexOf(target + '-') === 0) {
      var remainder = cardHandle.slice(target.length + 1);
      if (/^\d+$/.test(remainder)) {
        return true;
      }
    }

    return false;
  }

  function isCollectionVideoCard(card) {
    return !!(card && card.classList && card.classList.contains('product-card--collection-video'));
  }

  function isMaterialSplitCard(card) {
    return !!(card && card.getAttribute('data-split-variant-id'));
  }

  var VIDEO_SPLIT_TEMPLATES = ['split-cards-width-video', 'men-s-collection'];
  var FEATURED_SPLIT_TEMPLATES = ['split-cards-collection', 'split-cards-width-video', 'men-s-collection'];

  function isVideoSplitTemplate(productList) {
    return VIDEO_SPLIT_TEMPLATES.indexOf(getCollectionTemplate(productList)) !== -1;
  }

  function hasCollectionProductVideos(productList) {
    if (!productList) {
      return false;
    }

    if (productList.getAttribute('data-collection-product-videos') === 'true') {
      return true;
    }

    return isVideoSplitTemplate(productList);
  }

  function cardsIncludeVideo(cards) {
    if (!cards || !cards.length) {
      return false;
    }

    for (var i = 0; i < cards.length; i++) {
      if (isCollectionVideoCard(cards[i])) {
        return true;
      }
    }

    return false;
  }

  function refreshFeaturedCardMedia(productList, cards) {
    var list = cards || [];

    for (var i = 0; i < list.length; i++) {
      var card = list[i];
      if (!card) continue;

      if (!isCollectionVideoCard(card)) {
        initProductCard(card);
      }

      if (
        window.CollectionPromoCards &&
        typeof window.CollectionPromoCards.init === 'function'
      ) {
        window.CollectionPromoCards.init(card);
      }
    }

    if (
      window.CollectionPromoCards &&
      typeof window.CollectionPromoCards.recoverVisibleVideos === 'function'
    ) {
      window.CollectionPromoCards.recoverVisibleVideos();
    } else if (
      productList &&
      window.CollectionPromoCards &&
      typeof window.CollectionPromoCards.scheduleHeightSync === 'function'
    ) {
      window.CollectionPromoCards.scheduleHeightSync(productList);
    }
  }

  function getCollectionTemplate(productList) {
    if (!productList) {
      return '';
    }

    if (productList.getAttribute('data-collection-template')) {
      return productList.getAttribute('data-collection-template');
    }

    var main = getCollectionMain(productList);
    return main && main.getAttribute('data-collection-template')
      ? main.getAttribute('data-collection-template')
      : '';
  }

  function isSplitCardsCollectionTemplate(productList) {
    return getCollectionTemplate(productList) === 'split-cards-collection';
  }

  function isExternalFeaturedProduct(productList) {
    return productList && productList.getAttribute('data-featured-product-external') === 'true';
  }

  function isFeaturedSplitByMaterial(productList) {
    if (!productList) {
      return false;
    }

    if (productList.getAttribute('data-featured-split-by-material') === 'true') {
      return true;
    }

    return FEATURED_SPLIT_TEMPLATES.indexOf(getCollectionTemplate(productList)) !== -1;
  }

  function isSplitCardsMode(productList) {
    if (productList && productList.getAttribute('data-split-cards-pagination') === 'true') {
      return true;
    }

    return isSplitCardsPagination(getCollectionMain(productList));
  }

  function getMaterialSplitCardsForHandle(productList, handle) {
    return findAllProductCardsForHandle(productList, handle).filter(isMaterialSplitCard);
  }

  function getFeaturedMaterialCards(productList, handle) {
    return findFeaturedCards(productList, handle).filter(function (card) {
      return !isCollectionVideoCard(card) && isMaterialSplitCard(card);
    });
  }

  function orderPinnedCards(cards) {
    var videos = [];
    var materials = [];
    var others = [];

    cards.forEach(function (card) {
      if (isCollectionVideoCard(card)) {
        videos.push(card);
      } else if (isMaterialSplitCard(card)) {
        materials.push(card);
      } else {
        others.push(card);
      }
    });

    return videos.concat(materials, others);
  }

  function findProductCard(productList, handle, options) {
    options = options || {};
    var target = normalizeHandle(handle);
    if (!target) return null;

    var cards = productList.querySelectorAll('product-card');
    var videoFallback = null;

    for (var i = 0; i < cards.length; i++) {
      if (isPlaceholderCard(cards[i])) continue;
      if (!cardMatchesProductHandle(cards[i], target)) continue;

      if (isCollectionVideoCard(cards[i])) {
        if (!videoFallback) {
          videoFallback = cards[i];
        }
        continue;
      }

      return cards[i];
    }

    return options.allowVideoOnly ? videoFallback : null;
  }

  function findFeaturedCard(productList, handle) {
    var cards = findFeaturedCards(productList, handle);
    return cards.length ? cards[0] : null;
  }

  function findFeaturedCards(productList, handle) {
    var target = normalizeHandle(handle);
    if (!target) return [];

    var cards = productList.querySelectorAll('product-card[data-featured-product="true"]');
    var matches = [];

    for (var i = 0; i < cards.length; i++) {
      if (normalizeHandle(cards[i].getAttribute('data-featured-product-handle')) === target) {
        matches.push(cards[i]);
      }
    }

    return matches;
  }

  function findAllProductCardsForHandle(productList, handle) {
    var target = normalizeHandle(handle);
    if (!target) return [];

    var matches = [];
    var cards = productList.querySelectorAll('product-card');

    for (var i = 0; i < cards.length; i++) {
      if (isPlaceholderCard(cards[i])) continue;
      if (cardMatchesProductHandle(cards[i], target)) {
        matches.push(cards[i]);
      }
    }

    return matches;
  }

  function isFeaturedResolved(productList, handle) {
    if (isFeaturedSplitByMaterial(productList)) {
      var featuredMaterialCards = getFeaturedMaterialCards(productList, handle);

      if (featuredMaterialCards.length) {
        return !shouldRefetchSplitFeaturedCards(productList, handle);
      }

      var featuredCards = findFeaturedCards(productList, handle).filter(function (card) {
        return !isCollectionVideoCard(card);
      });

      if (featuredCards.length) {
        return !shouldRefetchSplitFeaturedCards(productList, handle);
      }

      return false;
    }

    if (findFeaturedCards(productList, handle).length) {
      return true;
    }

    return !!findProductCard(productList, handle, { allowVideoOnly: true });
  }

  function shouldRefetchSplitFeaturedCards(productList, handle) {
    if (!isFeaturedSplitByMaterial(productList)) {
      return false;
    }

    var featured = findFeaturedCards(productList, handle).filter(function (card) {
      return !isCollectionVideoCard(card);
    });

    if (!featured.length) {
      return false;
    }

    var splitCards = featured.filter(isMaterialSplitCard);

    if (splitCards.length > 1) {
      return false;
    }

    if (splitCards.length === 1) {
      return featured.length > 1;
    }

    return featured.length > 1;
  }

  function removeFeaturedCards(productList, handle) {
    findFeaturedCards(productList, handle).forEach(function (card) {
      card.remove();
    });
  }

  function getAdBadgeText(productList) {
    // p1-p20 multi-pin mode: no red ad badge
    if (isPParamMode()) {
      return '';
    }

    var fromTheme =
      window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.featuredProductAdBadgeText;

    if (fromTheme !== undefined && fromTheme !== null) {
      return String(fromTheme).trim();
    }

    if (productList && productList.hasAttribute('data-featured-product-ad-badge')) {
      var fromAttr = (productList.getAttribute('data-featured-product-ad-badge') || '').trim();
      if (fromAttr) {
        return fromAttr;
      }
    }

    return 'PRODUCT FROM AD';
  }

  function ensureAdBadge(card, productList) {
    var text = getAdBadgeText(productList);
    var existing = card.querySelector('.product-card__ad-badge');

    if (!text) {
      if (existing) {
        existing.remove();
      }
      return;
    }

    var figure = card.querySelector('.product-card__figure');
    if (!figure) return;

    var badge = existing;
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'product-card__ad-badge';
      badge.setAttribute('aria-label', text);
      figure.appendChild(badge);
    }

    badge.textContent = text;
  }

  function markFeatured(card, handle, productList) {
    card.setAttribute('data-featured-product', 'true');
    card.setAttribute('data-featured-product-handle', handle);
    ensureAdBadge(card, productList);
  }

  function removeDuplicateCards(productList, handle, keepCards) {
    var target = normalizeHandle(handle);
    var keepList = [];

    if (keepCards) {
      keepList = Array.isArray(keepCards) ? keepCards : [keepCards];
    }

    var keepSet = new Set(keepList);
    var keepVariantIds = new Set();

    for (var k = 0; k < keepList.length; k++) {
      var keepVariantId = keepList[k].getAttribute('data-split-variant-id');
      if (keepVariantId) {
        keepVariantIds.add(keepVariantId);
      }
    }

    var cards = productList.querySelectorAll('product-card');
    for (var i = 0; i < cards.length; i++) {
      if (keepSet.has(cards[i]) || isPlaceholderCard(cards[i])) continue;
      if (!cardMatchesProductHandle(cards[i], target)) continue;

      var cardVariantId = cards[i].getAttribute('data-split-variant-id');
      if (keepVariantIds.size && cardVariantId) {
        if (keepVariantIds.has(cardVariantId)) {
          cards[i].remove();
        }
        continue;
      }

      cards[i].remove();
    }
  }

  function initProductCard(card) {
    if (window.initProductCardVariants) {
      window.initProductCardVariants(card);
    }
  }

  function productPath(handle) {
    return getShopifyRoot() + 'products/' + handle;
  }

  function getProductCardSubtitle(productList) {
    if (!productList) {
      return '';
    }

    var subtitle = productList.getAttribute('data-product-card-subtitle');
    return subtitle && subtitle.trim() ? subtitle.trim() : '';
  }

  function parseProductCardFromHtml(html) {
    return parseProductCardsFromHtml(html)[0] || null;
  }

  function parseProductCardsFromHtml(html) {
    if (!html) return [];

    var lower = html.toLowerCase();
    if (lower.indexOf('<product-card') === -1) {
      return [];
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var cards = doc.querySelectorAll('product-card');
    if (cards.length) {
      return Array.prototype.slice.call(cards);
    }

    var wrapped = new DOMParser().parseFromString(
      '<div id="featured-product-parse-root">' + html + '</div>',
      'text/html'
    );
    cards = wrapped.querySelectorAll('product-card');
    return cards.length ? Array.prototype.slice.call(cards) : [];
  }

  function appendFeaturedFetchParams(url, productList) {
    if (productList && productList.getAttribute('data-hide-product-information') === 'true') {
      url.searchParams.set('hide_product_information', 'true');
    }

    var cardSubtitle = getProductCardSubtitle(productList);
    if (cardSubtitle) {
      url.searchParams.set('card_subtitle', cardSubtitle);
    }

    return url;
  }

  function getFetchUrls(handle, productList, options) {
    options = options || {};
    var splitByMaterial =
      options.splitByMaterial !== undefined
        ? options.splitByMaterial
        : isFeaturedSplitByMaterial(productList);
    var urls = [];
    var sectionId = FEATURED_SECTION_ID;
    var viewName = 'featured-collection-card';
    var externalSectionId = FEATURED_EXTERNAL_SECTION_ID;

    if (splitByMaterial) {
      if (options.forceVideoSplit || hasCollectionProductVideos(productList)) {
        sectionId = FEATURED_SECTION_VIDEO_SPLIT_ID;
        viewName = 'featured-collection-card-video-split';
        externalSectionId = FEATURED_EXTERNAL_VIDEO_SPLIT_ID;
      } else {
        sectionId = FEATURED_SECTION_SPLIT_ID;
        viewName = 'featured-collection-card-split';
        externalSectionId = FEATURED_EXTERNAL_SECTION_SPLIT_ID;
      }
    }

    var sectionUrl = new URL(productPath(handle), window.location.origin);
    sectionUrl.searchParams.set('section_id', sectionId);
    urls.push(appendFeaturedFetchParams(sectionUrl, productList).toString());

    var viewUrl = new URL(productPath(handle), window.location.origin);
    viewUrl.searchParams.set('view', viewName);
    urls.push(appendFeaturedFetchParams(viewUrl, productList).toString());

    var collectionUrl = new URL(window.location.pathname, window.location.origin);
    collectionUrl.searchParams.set('section_id', externalSectionId);
    collectionUrl.searchParams.set(PARAM, handle);
    urls.push(appendFeaturedFetchParams(collectionUrl, productList).toString());

    return urls;
  }

  function fetchProductCardHtml(handle, productList, options) {
    var urls = getFetchUrls(handle, productList, options);
    var index = 0;

    function tryNext() {
      if (index >= urls.length) {
        return Promise.resolve(null);
      }

      var url = urls[index];
      index += 1;

      return fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html,application/xhtml+xml',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(function (response) {
          if (!response.ok) {
            return tryNext();
          }
          return response.text();
        })
        .then(function (html) {
          if (!html) {
            return tryNext();
          }
          var cards = parseProductCardsFromHtml(html);
          if (cards.length) {
            return cards.map(function (card) {
              return document.importNode(card, true);
            });
          }
          return tryNext();
        })
        .catch(function () {
          return tryNext();
        });
    }

    return tryNext();
  }

  function createPrefetchListStub(productList) {
    if (productList) {
      return productList;
    }

    var isCollectionPage = window.location.pathname.indexOf('/collections/') !== -1;

    function liveProductList() {
      return (
        document.querySelector(
          '.shopify-section--main-collection product-list[id^="product-list-"]'
        ) ||
        document.querySelector('.collection__main product-list[id^="product-list-"]') ||
        document.querySelector('product-list[data-collection-template]') ||
        document.querySelector('product-list[data-featured-split-by-material]')
      );
    }

    return {
      getAttribute: function (name) {
        var list = isCollectionPage ? liveProductList() : null;

        if (name === 'data-split-cards-pagination' && list) {
          if (list.getAttribute('data-split-cards-pagination') === 'true' || isFeaturedSplitByMaterial(list)) {
            return 'true';
          }
        }

        if (name === 'data-featured-split-by-material' && list) {
          if (list.getAttribute('data-featured-split-by-material') === 'true' || isFeaturedSplitByMaterial(list)) {
            return 'true';
          }
        }

        if (name === 'data-collection-template' && list) {
          return list.getAttribute('data-collection-template');
        }

        if (name === 'data-collection-product-videos' && list) {
          return list.getAttribute('data-collection-product-videos');
        }

        if (name === 'data-hide-product-information') {
          return list ? list.getAttribute('data-hide-product-information') || 'false' : 'false';
        }

        if (name === 'data-product-card-subtitle') {
          return list ? list.getAttribute('data-product-card-subtitle') || '' : '';
        }

        return null;
      }
    };
  }

  function startPrefetch(handle, productList) {
    if (!handle || prefetchCache[handle]) return;

    // Avoid caching the wrong (non-video) card HTML before the collection grid exists.
    if (!productList && window.location.pathname.indexOf('/collections/') !== -1) {
      var liveList =
        document.querySelector(
          '.shopify-section--main-collection product-list[id^="product-list-"]'
        ) || document.querySelector('.collection__main product-list[id^="product-list-"]');
      if (!liveList) {
        return;
      }
      productList = liveList;
    }

    var listStub = createPrefetchListStub(productList);

    prefetchCache[handle] = {
      html: fetchProductCardHtml(handle, listStub, {
        splitByMaterial: isFeaturedSplitByMaterial(listStub)
      })
    };
  }

  function fetchAndInsertFeatured(productList, handle) {
    clearPrefetch(handle);

    return fetchProductCardWithFallback(handle, productList).then(function (cards) {
      return !!(cards && cards.length && insertFetchedCard(handle, productList, cards));
    });
  }

  function ensureFeaturedFromSection(productList, handle) {
    return fetchAndInsertFeatured(productList, handle).then(function () {
      if (isFeaturedResolved(productList, handle)) {
        removePlaceholder(productList);
        return;
      }

      return continueFeaturedGridAndPages(productList, handle);
    });
  }

  function continueFeaturedGridAndPages(productList, handle) {
    var existing = findProductCard(productList, handle, {
      allowVideoOnly: isVideoSplitTemplate(productList) || hasCollectionProductVideos(productList)
    });

    if (existing) {
      removePlaceholder(productList);
      pinExistingCard(existing, handle, productList);
      return Promise.resolve(true);
    }

    var hasPlaceholder = !!productList.querySelector('[data-featured-product-placeholder]');

    // Multi-pin (2–20 products): never auto-load collection pages looking for products.
    // That burns infinite-scroll pagination and still fails for out-of-collection handles.
    // Fetch card HTML directly instead.
    if (getFeaturedHandles().length > 1) {
      return insertFetchedFeaturedProduct(handle, productList).then(function (inserted) {
        if (inserted) {
          removePlaceholder(productList);
        }
        return !!inserted;
      });
    }

    return loadPagesUntilFeaturedFound(productList, handle).then(function () {
      if (isFeaturedResolved(productList, handle)) {
        notifyProductsAppended(productList, []);
        return true;
      }

      if (hasPlaceholder || !isFeaturedResolved(productList, handle)) {
        return insertFetchedFeaturedProduct(handle, productList).then(function (inserted) {
          if (inserted) {
            syncFeaturedProductState(productList);
          }
          return !!inserted;
        });
      }

      notifyProductsAppended(productList, []);
      return false;
    });
  }

  function syncFeaturedProductState(productList) {
    var handles = getFeaturedHandles();
    if (!handles.length) {
      var pendingHandle = productList.getAttribute('data-featured-product-handle');
      if (pendingHandle) {
        handles = [pendingHandle];
      }
    }

    if (!handles.length || isDisabled(productList) || productList._featuredPinning) {
      return;
    }

    var needsEnsure = false;

    for (var i = 0; i < handles.length; i++) {
      var handle = handles[i];

      if (isFeaturedResolved(productList, handle)) {
        // Resolved but video still missing on a video-enabled collection.
        if (
          hasCollectionProductVideos(productList) &&
          !cardsIncludeVideo(findAllProductCardsForHandle(productList, handle)) &&
          !(productList._featuredNoVideo && productList._featuredNoVideo[normalizeHandle(handle)])
        ) {
          restoreMissingFeaturedVideo(productList, handle);
        }
        continue;
      }

      var existing = findProductCard(productList, handle, {
        allowVideoOnly: isVideoSplitTemplate(productList) || hasCollectionProductVideos(productList)
      });

      if (existing) {
        removePlaceholder(productList);
        pinExistingCard(existing, handle, productList);
        restoreMissingFeaturedVideo(productList, handle);
      } else {
        needsEnsure = true;
      }
    }

    if (!needsEnsure) {
      removePlaceholder(productList);
      return;
    }

    var listKey = productList.id || 'default';
    if (!ensurePromises[listKey]) {
      ensureFeaturedProduct(productList);
    }
  }

  function fetchProductCardWithFallback(handle, productList) {
    var splitByMaterial = isFeaturedSplitByMaterial(productList);
    var wantVideos = hasCollectionProductVideos(productList);
    var cached = prefetchCache[handle];

    var fetchPromise = cached
      ? cached.html
      : fetchProductCardHtml(handle, productList, { splitByMaterial: splitByMaterial });

    return fetchPromise.then(function (cards) {
      if (cards && cards.length) {
        // Prefetch may have run before video mode was known — refetch video-split HTML.
        if (wantVideos && splitByMaterial && !cardsIncludeVideo(cards)) {
          clearPrefetch(handle);
          return fetchProductCardHtml(handle, productList, {
            splitByMaterial: true,
            forceVideoSplit: true
          }).then(function (videoCards) {
            return videoCards && videoCards.length ? videoCards : cards;
          });
        }

        return cards;
      }

      if (!splitByMaterial) {
        return [];
      }

      clearPrefetch(handle);
      return fetchProductCardHtml(handle, productList, { splitByMaterial: false });
    });
  }

  function removePlaceholder(productList) {
    var placeholder = productList.querySelector('[data-featured-product-placeholder]');
    if (placeholder) {
      placeholder.remove();
    }
  }

  function getFeaturedInsertBefore(productList, handle) {
    var targetIndex = getPinnedHandleIndex(handle);

    if (targetIndex < 0) {
      return productList.firstElementChild;
    }

    var cards = productList.children;
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (!card || !card.tagName || card.tagName.toLowerCase() !== 'product-card') {
        continue;
      }
      if (isPlaceholderCard(card)) {
        continue;
      }

      if (card.getAttribute('data-featured-product') === 'true') {
        var cardHandle = normalizeHandle(card.getAttribute('data-featured-product-handle'));
        var cardIndex = getPinnedHandleIndex(cardHandle);

        if (cardIndex !== -1 && cardIndex <= targetIndex) {
          continue;
        }

        return card;
      }

      return card;
    }

    return null;
  }

  function insertFetchedCard(handle, productList, cards) {
    var cardList = cards;

    if (!cardList) {
      return false;
    }

    if (!Array.isArray(cardList)) {
      cardList = [cardList];
    }

    cardList = cardList.filter(Boolean);
    if (!cardList.length) {
      return false;
    }

    removePlaceholder(productList);

    var insertBefore = getFeaturedInsertBefore(productList, handle);
    for (var i = cardList.length - 1; i >= 0; i--) {
      var card = cardList[i];
      markFeatured(card, handle, productList);
      productList.insertBefore(card, insertBefore);
      card.removeAttribute('data-built-from-json');
      insertBefore = card;
    }

    productList.setAttribute('data-featured-product-external', 'true');
    refreshFeaturedCardMedia(productList, cardList);
    return true;
  }

  function clearPrefetch(handle) {
    if (handle) {
      delete prefetchCache[handle];
    }
  }

  function insertFetchedFeaturedProduct(handle, productList) {
    return fetchProductCardWithFallback(handle, productList).then(function (cards) {
      return insertFetchedCard(handle, productList, cards);
    });
  }

  function pinExistingCard(card, handle, productList) {
    var cardsToPin = [];

    // Always gather video + material cards for this handle so videos are not dropped.
    if (isFeaturedSplitByMaterial(productList) || hasCollectionProductVideos(productList)) {
      cardsToPin = orderPinnedCards(findAllProductCardsForHandle(productList, handle));
    }

    if (!cardsToPin.length && card) {
      cardsToPin = orderPinnedCards(findAllProductCardsForHandle(productList, handle));
      if (!cardsToPin.length) {
        cardsToPin = [card];
      }
    }

    if (!cardsToPin.length) {
      return null;
    }

    removePlaceholder(productList);
    removeDuplicateCards(productList, handle, cardsToPin);

    var insertBefore = getFeaturedInsertBefore(productList, handle);
    for (var i = cardsToPin.length - 1; i >= 0; i--) {
      // Always tag handle so multi-pin insert order can skip earlier featured video cards
      cardsToPin[i].setAttribute('data-featured-product', 'true');
      cardsToPin[i].setAttribute('data-featured-product-handle', handle);
      if (isCollectionVideoCard(cardsToPin[i])) {
        ensureAdBadge(cardsToPin[i], productList);
      } else {
        markFeatured(cardsToPin[i], handle, productList);
      }
      productList.insertBefore(cardsToPin[i], insertBefore);
      insertBefore = cardsToPin[i];
    }

    refreshFeaturedCardMedia(productList, cardsToPin);
    return cardsToPin[0];
  }

  function getCollectionMain(productList) {
    var main = productList.closest('.collection__main[data-infinite-scroll-section-id]');
    if (main) {
      return main;
    }

    return productList.closest('.collection__main');
  }

  function isSplitCardsPagination(main) {
    return main && main.getAttribute('data-split-cards-pagination') === 'true';
  }

  function getSplitCardsCursor(productList) {
    if (productList.dataset.splitFetchPage) {
      return {
        fetchPage: parseInt(productList.dataset.splitFetchPage || '1', 10),
        hasMore: productList.dataset.splitHasMore === 'true'
      };
    }

    var cursor = productList.querySelector('.js-split-cards-cursor');
    if (!cursor) {
      return null;
    }

    return readSplitCursorElement(cursor);
  }

  function readSplitCursorElement(cursor) {
    return {
      fetchPage: parseInt(cursor.getAttribute('data-split-fetch-page') || '1', 10),
      hasMore: cursor.getAttribute('data-split-has-more') === 'true'
    };
  }

  function writeSplitCursor(productList, cursor) {
    if (!cursor) {
      delete productList.dataset.splitFetchPage;
      delete productList.dataset.splitHasMore;
      removeSplitCardsCursor(productList);
      return;
    }

    productList.dataset.splitFetchPage = String(cursor.fetchPage);
    productList.dataset.splitHasMore = cursor.hasMore ? 'true' : 'false';
  }

  function hydrateSplitCursor(productList) {
    var cursor = productList.querySelector('.js-split-cards-cursor');
    if (!cursor || productList.dataset.splitFetchPage) {
      return;
    }

    writeSplitCursor(productList, readSplitCursorElement(cursor));
  }

  function getSplitCardKey(node) {
    if (!node || !node.tagName || node.tagName.toLowerCase() !== 'product-card') {
      return null;
    }

    var productHandle = node.getAttribute('data-split-product-handle');
    var materialName = node.getAttribute('data-split-material-name');
    if (productHandle && materialName) {
      return productHandle + '::' + materialName.toLowerCase().trim();
    }

    var handle = node.getAttribute('handle');
    if (handle && handle.indexOf('collection-product-video-') === 0) {
      return handle;
    }

    var variantId = node.getAttribute('data-split-variant-id');
    if (!variantId || !handle) {
      return null;
    }

    return handle + ':' + variantId;
  }

  function ensureSplitCardKeySet(productList) {
    if (!productList._splitCardKeys) {
      productList._splitCardKeys = new Set();
      var existingCards = productList.querySelectorAll('product-card[data-split-variant-id]');
      for (var i = 0; i < existingCards.length; i++) {
        var key = getSplitCardKey(existingCards[i]);
        if (key) {
          productList._splitCardKeys.add(key);
        }
      }
    }

    return productList._splitCardKeys;
  }

  function isDuplicateSplitCard(productList, node) {
    var key = getSplitCardKey(node);
    if (!key) {
      return false;
    }

    var keySet = ensureSplitCardKeySet(productList);
    if (keySet.has(key)) {
      return true;
    }

    keySet.add(key);
    return false;
  }

  function removeSplitCardsCursor(productList) {
    var existing = productList.querySelector('.js-split-cards-cursor');
    if (existing) {
      existing.remove();
    }
  }

  function syncSplitCardsCursor(productList, sourceList) {
    removeSplitCardsCursor(productList);
    var cursor = sourceList && sourceList.querySelector('.js-split-cards-cursor');
    if (cursor) {
      productList.appendChild(document.importNode(cursor, true));
      writeSplitCursor(productList, readSplitCursorElement(cursor));
      return;
    }

    writeSplitCursor(productList, null);
  }

  function getProductListById(root, productListId) {
    if (!root || !productListId) {
      return null;
    }

    if (root.nodeType === 9 && typeof root.getElementById === 'function') {
      return root.getElementById(productListId);
    }

    if (typeof root.querySelector === 'function') {
      return root.querySelector('[id="' + String(productListId).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]');
    }

    return null;
  }

  function getCollectionHasMore(productList) {
    return productList.getAttribute('data-collection-has-more') === 'true';
  }

  function collectionBatchHasMore(productList, main) {
    if (getCollectionHasMore(productList)) {
      return true;
    }

    if (!isSplitCardsPagination(main)) {
      return false;
    }

    var splitCursor = getSplitCardsCursor(productList);
    return !!(splitCursor && splitCursor.hasMore);
  }

  function syncPaginationFromList(productList, sourceList) {
    if (!sourceList) {
      return;
    }

    if (sourceList.hasAttribute('data-collection-next-page')) {
      productList.setAttribute('data-collection-next-page', sourceList.getAttribute('data-collection-next-page'));
    }

    if (sourceList.hasAttribute('data-collection-has-more')) {
      productList.setAttribute('data-collection-has-more', sourceList.getAttribute('data-collection-has-more'));
    }

    syncSplitCardsCursor(productList, sourceList);
  }

  function parseFetchedProductList(html, productListId) {
    if (!html) {
      return null;
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var productList = getProductListById(doc, productListId);

    if (!productList && typeof doc.querySelector === 'function') {
      productList = doc.querySelector('product-list[id="' + productListId.replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]');
    }

    return productList;
  }

  function getSectionHtmlFromResponse(data, sectionId) {
    if (!data) {
      return null;
    }

    if (typeof data === 'string') {
      return data;
    }

    if (data[sectionId]) {
      return data[sectionId];
    }

    var keys = Object.keys(data);
    if (keys.length === 1) {
      return data[keys[0]];
    }

    for (var i = 0; i < keys.length; i++) {
      if (keys[i] === sectionId || keys[i].indexOf(sectionId) !== -1 || sectionId.indexOf(keys[i]) !== -1) {
        return data[keys[i]];
      }
    }

    return null;
  }

  function buildCollectionFetchUrl(productList, page, splitCursor, useSectionId) {
    var main = getCollectionMain(productList);
    var sectionId = main.getAttribute('data-infinite-scroll-section-id');
    var url = new URL(window.location.pathname, window.location.origin);
    var current = new URL(window.location.href);

    current.searchParams.forEach(function (value, key) {
      if (key === 'page' || key === 'sections' || key === 'section_id') {
        return;
      }

      if (key.indexOf('split_') === 0) {
        return;
      }

      // Keep infinite-scroll fetches independent of featured pinning params.
      if (key === PARAM || /^p([1-9]|1[0-9]|20)$/.test(key)) {
        return;
      }

      url.searchParams.set(key, value);
    });

    var templateSuffix = main.getAttribute('data-collection-template');
    if (templateSuffix && !url.searchParams.has('view')) {
      url.searchParams.set('view', templateSuffix);
    }

    url.searchParams.set('page', String(page));

    if (useSectionId) {
      url.searchParams.set('section_id', sectionId);
    } else {
      url.searchParams.set('sections', sectionId);
    }

    return url;
  }

  function countRenderableCollectionCards(list) {
    var count = 0;

    for (var i = 0; i < list.children.length; i++) {
      var node = list.children[i];
      if (node.classList && node.classList.contains('js-split-cards-cursor')) {
        continue;
      }
      count += 1;
    }

    return count;
  }

  function getNextPage(productList) {
    return parseInt(productList.getAttribute('data-collection-next-page') || '2', 10);
  }

  function setNextPage(productList, page) {
    productList.setAttribute('data-collection-next-page', String(page));
  }

  function appendCollectionPageNodes(productList, newList) {
    var appended = [];
    for (var i = 0; i < newList.children.length; i++) {
      var node = document.importNode(newList.children[i], true);
      if (node.classList && node.classList.contains('js-split-cards-cursor')) {
        continue;
      }
      if (isDuplicateSplitCard(productList, node)) {
        continue;
      }
      productList.appendChild(node);
      if (node.hasAttribute && node.hasAttribute('reveal-on-scroll')) {
        node.removeAttribute('reveal-on-scroll');
      }
      initProductCard(node);
      appended.push(node);
    }
    syncSplitCardsCursor(productList, newList);
    syncPaginationFromList(productList, newList);
    return appended;
  }

  function onProductsAppended(productList, appendedNodes) {
    var handles = getFeaturedHandles();
    if (!handles.length || isDisabled(productList) || productList._featuredPinning) return;

    // Only re-pin handles that actually appear in newly appended nodes.
    // Empty appends (mutation observer noise) must not reshuffle featured cards —
    // that repeatedly breaks video tiles and fights infinite scroll.
    if (!appendedNodes || !appendedNodes.length) {
      return;
    }

    handles.forEach(function (handle) {
      var target = normalizeHandle(handle);
      var matchedNode = null;

      for (var i = 0; i < appendedNodes.length; i++) {
        var node = appendedNodes[i];
        if (
          node.tagName &&
          node.tagName.toLowerCase() === 'product-card' &&
          isRealProductCard(node) &&
          cardMatchesProductHandle(node, target)
        ) {
          matchedNode = node;
          break;
        }
      }

      if (!matchedNode) {
        return;
      }

      // Prefer a non-video card as the pin anchor when available.
      if (isCollectionVideoCard(matchedNode)) {
        var material = findProductCard(productList, handle, { allowVideoOnly: false });
        pinExistingCard(material || matchedNode, handle, productList);
      } else {
        pinExistingCard(matchedNode, handle, productList);
      }

      restoreMissingFeaturedVideo(productList, handle);
    });
  }

  function notifyProductsAppended(productList, appendedNodes) {
    onProductsAppended(productList, appendedNodes);
    syncFeaturedProductState(productList);

    if (
      appendedNodes &&
      appendedNodes.length &&
      window.CollectionPromoCards &&
      typeof window.CollectionPromoCards.init === 'function'
    ) {
      for (var i = 0; i < appendedNodes.length; i++) {
        window.CollectionPromoCards.init(appendedNodes[i]);
      }

      if (typeof window.CollectionPromoCards.scheduleHeightSync === 'function') {
        window.CollectionPromoCards.scheduleHeightSync(productList);
      } else if (typeof window.CollectionPromoCards.syncHeights === 'function') {
        window.CollectionPromoCards.syncHeights(productList);
      }

      if (typeof window.CollectionPromoCards.recoverVisibleVideos === 'function') {
        window.CollectionPromoCards.recoverVisibleVideos();
      }
    }
  }

  function fetchCollectionPage(productList, page) {
    var main = getCollectionMain(productList);
    if (!main) return Promise.resolve(null);

    var sectionId = main.getAttribute('data-infinite-scroll-section-id');
    var url = buildCollectionFetchUrl(productList, page, null, true);

    return fetch(url.toString(), {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html, application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then(function (response) {
        if (!response.ok) {
          return null;
        }

        var contentType = response.headers.get('content-type') || '';

        if (contentType.indexOf('application/json') !== -1) {
          return response.json().then(function (data) {
            return getSectionHtmlFromResponse(data, sectionId);
          });
        }

        return response.text();
      })
      .catch(function () {
        return null;
      });
  }

  function loadPagesUntilFeaturedFound(productList, handle) {
    if (isFeaturedResolved(productList, handle)) {
      notifyProductsAppended(productList, []);
      return Promise.resolve(true);
    }

    var main = getCollectionMain(productList);
    if (!main) {
      return Promise.resolve(false);
    }

    var perPage = parseInt(main.getAttribute('data-infinite-scroll-per-page'), 10) || 24;
    var pagesLoaded = 0;

    function loadNext() {
      if (isFeaturedResolved(productList, handle)) {
        notifyProductsAppended(productList, []);
        return Promise.resolve(true);
      }

      if (pagesLoaded >= MAX_AUTO_PAGES) {
        return Promise.resolve(false);
      }

      if (!collectionBatchHasMore(productList, main)) {
        return Promise.resolve(false);
      }

      var page = getNextPage(productList);

      return fetchCollectionPage(productList, page).then(function (html) {
        pagesLoaded += 1;

        if (!html) {
          return false;
        }

        var newList = parseFetchedProductList(html, productList.id);
        if (!newList || countRenderableCollectionCards(newList) === 0) {
          return false;
        }

        var appended = appendCollectionPageNodes(productList, newList);
        notifyProductsAppended(productList, appended);

        if (isFeaturedResolved(productList, handle)) {
          return true;
        }

        if (!collectionBatchHasMore(productList, main)) {
          return false;
        }

        if (!isSplitCardsPagination(main) && countRenderableCollectionCards(newList) < perPage) {
          return false;
        }

        if (appended.length === 0) {
          return false;
        }

        return loadNext();
      });
    }

    return loadNext();
  }

  function ensureFeaturedProduct(productList) {
    var handles = getFeaturedHandles();
    if (!handles.length) {
      var pendingHandle = productList.getAttribute('data-featured-product-handle');
      if (pendingHandle) {
        handles = [pendingHandle];
      }
    }

    if (!handles.length || isDisabled(productList)) {
      return Promise.resolve();
    }

    var listKey = productList.id || 'default';
    if (ensurePromises[listKey]) {
      return ensurePromises[listKey];
    }

    productList._featuredPinning = true;

    ensurePromises[listKey] = handles
      .reduce(function (promise, handle) {
        return promise.then(function () {
          return ensureFeaturedProductWork(productList, handle);
        });
      }, Promise.resolve())
      .then(function () {
        // Final ordered pass + video recovery for every handle.
        return handles.reduce(function (promise, handle) {
          return promise.then(function () {
            var cards = findAllProductCardsForHandle(productList, handle);
            if (cards.length) {
              pinExistingCard(cards[0], handle, productList);
            }
            return restoreMissingFeaturedVideo(productList, handle);
          });
        }, Promise.resolve());
      })
      .finally(function () {
        productList._featuredPinning = false;
        delete ensurePromises[listKey];
        refreshFeaturedCardMedia(
          productList,
          productList.querySelectorAll('product-card[data-featured-product="true"]')
        );
      });

    return ensurePromises[listKey];
  }

  function ensureFeaturedProductWork(productList, handle) {
    startPrefetch(handle, productList);

    var featuredMaterialCards = getFeaturedMaterialCards(productList, handle);
    var featuredCards = findFeaturedCards(productList, handle).filter(function (card) {
      return !isCollectionVideoCard(card);
    });

    if (featuredMaterialCards.length || featuredCards.length) {
      if (shouldRefetchSplitFeaturedCards(productList, handle)) {
        removeFeaturedCards(productList, handle);
      } else {
        removePlaceholder(productList);

        if (getFeaturedHandles().length > 1) {
          var alreadyPinned = featuredMaterialCards[0] || featuredCards[0];
          if (alreadyPinned) {
            pinExistingCard(alreadyPinned, handle, productList);
          }
          return restoreMissingFeaturedVideo(productList, handle);
        }

        var cardsToBadge = featuredMaterialCards.length ? featuredMaterialCards : featuredCards;
        cardsToBadge.forEach(function (card) {
          ensureAdBadge(card, productList);
        });
        orderPinnedCards(findAllProductCardsForHandle(productList, handle))
          .filter(isCollectionVideoCard)
          .forEach(function (card) {
            ensureAdBadge(card, productList);
          });
        return Promise.resolve();
      }
    }

    var existing = findProductCard(productList, handle, {
      allowVideoOnly: isVideoSplitTemplate(productList) || hasCollectionProductVideos(productList)
    });

    if (existing) {
      removePlaceholder(productList);
      pinExistingCard(existing, handle, productList);
      return restoreMissingFeaturedVideo(productList, handle);
    }

    return ensureFeaturedFromSection(productList, handle).then(function () {
      return restoreMissingFeaturedVideo(productList, handle);
    });
  }

  function restoreMissingFeaturedVideo(productList, handle) {
    if (!hasCollectionProductVideos(productList)) {
      return Promise.resolve();
    }

    if (!productList._featuredNoVideo) {
      productList._featuredNoVideo = {};
    }

    var handleKey = normalizeHandle(handle);
    var relatedCards = findAllProductCardsForHandle(productList, handle);
    if (cardsIncludeVideo(relatedCards)) {
      refreshFeaturedCardMedia(productList, relatedCards.filter(isCollectionVideoCard));
      return Promise.resolve();
    }

    if (productList._featuredNoVideo[handleKey]) {
      return Promise.resolve();
    }

    if (productList._featuredVideoRestoring && productList._featuredVideoRestoring[handleKey]) {
      return productList._featuredVideoRestoring[handleKey];
    }

    if (!productList._featuredVideoRestoring) {
      productList._featuredVideoRestoring = {};
    }

    productList._featuredVideoRestoring[handleKey] = fetchProductCardHtml(handle, productList, {
      splitByMaterial: true,
      forceVideoSplit: true
    })
      .then(function (cards) {
        if (!cards || !cards.length) {
          productList._featuredNoVideo[handleKey] = true;
          return;
        }

        var videoCards = cards.filter(isCollectionVideoCard);
        if (!videoCards.length) {
          productList._featuredNoVideo[handleKey] = true;
          return;
        }

        // Avoid duplicate video tiles for the same product.
        var existingVideos = findAllProductCardsForHandle(productList, handle).filter(isCollectionVideoCard);
        existingVideos.forEach(function (card) {
          card.remove();
        });

        var insertBefore = null;
        var currentCards = findAllProductCardsForHandle(productList, handle);
        if (currentCards.length) {
          insertBefore = currentCards[0];
        } else {
          insertBefore = getFeaturedInsertBefore(productList, handle);
        }

        for (var i = videoCards.length - 1; i >= 0; i--) {
          var videoCard = videoCards[i];
          videoCard.setAttribute('data-featured-product', 'true');
          videoCard.setAttribute('data-featured-product-handle', handle);
          ensureAdBadge(videoCard, productList);
          productList.insertBefore(videoCard, insertBefore);
          insertBefore = videoCard;
        }

        refreshFeaturedCardMedia(productList, videoCards);
      })
      .finally(function () {
        if (productList._featuredVideoRestoring) {
          delete productList._featuredVideoRestoring[handleKey];
        }
      });

    return productList._featuredVideoRestoring[handleKey];
  }

  function observeProductList(productList) {
    if (productList.getAttribute('data-featured-product-observed') === 'true') {
      return;
    }
    productList.setAttribute('data-featured-product-observed', 'true');

    var syncTimer = null;
    var observer = new MutationObserver(function () {
      if (productList._featuredPinning) {
        return;
      }
      clearTimeout(syncTimer);
      syncTimer = setTimeout(function () {
        if (productList._featuredPinning) {
          return;
        }
        // Light sync only — do not reshuffle every featured product on each DOM tweak.
        syncFeaturedProductState(productList);
      }, 80);
    });
    observer.observe(productList, { childList: true });
  }

  var scrollObserver = null;
  var scrollListener = null;
  var infiniteScrollStates = {};
  var initInfiniteScrollTimer = null;

  function pokeInfiniteScrollSentinel(sentinel, loadNext) {
    if (!sentinel || !loadNext || sentinel.style.display === 'none') {
      return;
    }

    var rect = sentinel.getBoundingClientRect();
    if (rect.top <= window.innerHeight + 240) {
      requestAnimationFrame(loadNext);
    }
  }

  function shouldReinitInfiniteScroll(section, mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var mutation = mutations[i];

      if (mutation.target === section) {
        return true;
      }

      if (
        mutation.target.classList &&
        mutation.target.classList.contains('collection__main')
      ) {
        return true;
      }
    }

    return false;
  }

  function resetSplitCardDedupe(productList) {
    if (productList) {
      delete productList._splitCardKeys;
    }
  }

  function getInfiniteScrollState(productListId) {
    if (!infiniteScrollStates[productListId]) {
      infiniteScrollStates[productListId] = { loading: false, done: false };
    }
    return infiniteScrollStates[productListId];
  }

  function cursorsEqual(left, right) {
    if (!left || !right) {
      return false;
    }

    return left.fetchPage === right.fetchPage;
  }

  function resetInfiniteScrollState(productListId, hasMore) {
    infiniteScrollStates[productListId] = {
      loading: false,
      done: hasMore === false
    };
  }

  function initInfiniteScroll() {
    var main = document.querySelector('.shopify-section--main-collection .collection__main[data-infinite-scroll-section-id]');
    if (!main) {
      main = document.querySelector('.collection__main[data-infinite-scroll-section-id]');
    }
    if (!main) return;

    if (scrollObserver) {
      scrollObserver.disconnect();
      scrollObserver = null;
    }

    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener);
      scrollListener = null;
    }

    var sectionId = main.getAttribute('data-infinite-scroll-section-id');
    var perPage = parseInt(main.getAttribute('data-infinite-scroll-per-page'), 10) || 24;
    var splitPagination = isSplitCardsPagination(main);
    var productListId = 'product-list-' + sectionId;
    var productList = document.getElementById(productListId);
    var sentinel = main.querySelector('.collection-infinite-scroll-sentinel');
    if (!productList || !sentinel) return;

    hydrateSplitCursor(productList);

    var hasMore = getCollectionHasMore(productList);
    if (!hasMore && splitPagination) {
      var initialCursor = getSplitCardsCursor(productList);
      hasMore = !!(initialCursor && initialCursor.hasMore);
      if (hasMore) {
        productList.setAttribute('data-collection-has-more', 'true');
      }
    }

    if (!hasMore && !splitPagination) {
      hasMore = true;
    }

    resetInfiniteScrollState(productListId, hasMore);

    var scrollState = getInfiniteScrollState(productListId);
    sentinel.style.display = '';
    var loading = false;
    var done = !hasMore;
    scrollState.done = done;

    if (done) {
      sentinel.style.display = 'none';
    }

    function loadNext() {
      if (loading || done || scrollState.loading || scrollState.done) return;

      if (!getCollectionHasMore(productList)) {
        done = true;
        scrollState.done = true;
        sentinel.style.display = 'none';
        return;
      }

      loading = true;
      scrollState.loading = true;

      var page = getNextPage(productList);

      fetchCollectionPage(productList, page)
        .then(function (html) {
          if (!html) {
            return;
          }

          var newList = parseFetchedProductList(html, productListId);
          if (!newList || countRenderableCollectionCards(newList) === 0) {
            return;
          }

          var appended = appendCollectionPageNodes(productList, newList);
          notifyProductsAppended(productList, appended);

          hasMore = getCollectionHasMore(productList);
          if (!hasMore && splitPagination) {
            var nextCursor = getSplitCardsCursor(productList);
            hasMore = !!(nextCursor && nextCursor.hasMore);
          }

          if (!hasMore || appended.length === 0) {
            done = true;
            scrollState.done = true;
            sentinel.style.display = 'none';
            productList.setAttribute('data-collection-has-more', 'false');
          } else {
            done = false;
            scrollState.done = false;
            sentinel.style.display = '';
          }
        })
        .catch(function () {
          /* keep scroll active so a later scroll can retry */
        })
        .finally(function () {
          loading = false;
          scrollState.loading = false;
          scrollState.done = done;

          if (!done) {
            pokeInfiniteScrollSentinel(sentinel, loadNext);
          }
        });
    }

    main._collectionLoadNext = loadNext;

    scrollObserver = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          loadNext();
        }
      },
      { rootMargin: '400px', threshold: 0 }
    );
    scrollObserver.observe(sentinel);

    scrollListener = function () {
      pokeInfiniteScrollSentinel(sentinel, loadNext);
    };
    window.addEventListener('scroll', scrollListener, { passive: true });

    requestAnimationFrame(function () {
      pokeInfiniteScrollSentinel(sentinel, loadNext);
    });
  }

  function scheduleInitInfiniteScroll() {
    clearTimeout(initInfiniteScrollTimer);
    initInfiniteScrollTimer = setTimeout(initInfiniteScroll, 50);
  }

  function getMainCollectionProductLists() {
    var lists = document.querySelectorAll('.shopify-section--main-collection product-list[id^="product-list-"]');
    if (lists.length) {
      return lists;
    }
    return document.querySelectorAll('.collection__main product-list[id^="product-list-"]');
  }

  function initProductList(productList) {
    if (!productList.id || productList.id.indexOf('product-list-') !== 0) {
      return;
    }

    if (isFilteringOrSortingActive(productList)) {
      productList.setAttribute('data-featured-product-disabled', 'true');
      return;
    }

    if (!getFeaturedHandles().length && !productList.getAttribute('data-featured-product-handle')) {
      return;
    }

    ensureFeaturedProduct(productList).then(function () {
      observeProductList(productList);
    });
  }

  function init() {
    var lists = getMainCollectionProductLists();
    for (var i = 0; i < lists.length; i++) {
      hydrateSplitCursor(lists[i]);
      initProductList(lists[i]);
    }

    initInfiniteScroll();

    var section =
      document.querySelector('.shopify-section--main-collection') ||
      (document.querySelector('.collection__main') && document.querySelector('.collection__main').closest('.shopify-section'));

    if (section && !section.getAttribute('data-infinite-scroll-watch')) {
      section.setAttribute('data-infinite-scroll-watch', 'true');
      var mo = new MutationObserver(function (mutations) {
        if (!shouldReinitInfiniteScroll(section, mutations)) {
          return;
        }

        var updatedLists = getMainCollectionProductLists();
        for (var j = 0; j < updatedLists.length; j++) {
          resetSplitCardDedupe(updatedLists[j]);
          hydrateSplitCursor(updatedLists[j]);
          initProductList(updatedLists[j]);
        }

        scheduleInitInfiniteScroll();
      });
      mo.observe(section, { childList: true, subtree: true });
    }

    document.documentElement.addEventListener('theme:loading:end', function () {
      if (!document.querySelector('.collection__main[data-infinite-scroll-section-id]')) {
        return;
      }

      var updatedLists = getMainCollectionProductLists();
      for (var k = 0; k < updatedLists.length; k++) {
        resetSplitCardDedupe(updatedLists[k]);
        hydrateSplitCursor(updatedLists[k]);
      }

      scheduleInitInfiniteScroll();
    });
  }

  window.FeaturedProductCollection = {
    init: init,
    sync: function (productList, options) {
      if (options && options.skipFetch) {
        onProductsAppended(productList, []);
        return Promise.resolve(true);
      }
      return ensureFeaturedProduct(productList);
    },
    apply: function (productList) {
      return ensureFeaturedProduct(productList);
    },
    dedupe: function (productList) {
      notifyProductsAppended(productList, []);
    },
    onProductsAppended: notifyProductsAppended,
    initInfiniteScroll: initInfiniteScroll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function maybeStartEarlyPrefetch() {
    var list =
      document.querySelector(
        '.shopify-section--main-collection product-list[id^="product-list-"]'
      ) || document.querySelector('.collection__main product-list[id^="product-list-"]');

    if (!list) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', maybeStartEarlyPrefetch);
      }
      return;
    }

    var earlyHandles = getFeaturedHandles();
    for (var earlyIndex = 0; earlyIndex < earlyHandles.length; earlyIndex++) {
      startPrefetch(earlyHandles[earlyIndex], list);
    }
  }

  maybeStartEarlyPrefetch();
})();
